import {
    areSearchParamsEqual,
    CALLS_ENCODED_QUERY_KEY,
    getHashSearchParams,
    unwrapEncodedSearchParams,
    wrapEncodedSearchParams,
} from './encodedUrlQuery';

export { areSearchParamsEqual };

export type CallsStatisticsFilterItem = { key: string; value: string };

export type CallsStatisticsUrlViewState = {
    hasAny: boolean;
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    selectedBranchId: number | null;
    filterFullName: CallsStatisticsFilterItem[] | null;
    filterDateTime: Date | null;
    filterEndDateTime: Date | null;
    startTime: Date | null;
    endTime: Date | null;
    hasCustomDateRange: boolean;
    uiFilters: Record<string, any>;
};

export type CallsStatisticsUrlWriteState = {
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    selectedBranchId: number | null;
    filterFullName: CallsStatisticsFilterItem[] | null;
    filterDateTime: any;
    filterEndDateTime: any;
    startTime: any;
    endTime: any;
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

export function readInitialCallsStatisticsUrlState(): CallsStatisticsUrlViewState {
    return parseCallsStatisticsUrlState(getHashSearchParams());
}

export function parseCallsStatisticsUrlState(
    params: URLSearchParams,
    encodedKey: string = CALLS_ENCODED_QUERY_KEY
): CallsStatisticsUrlViewState {
    params = unwrapEncodedSearchParams(params, encodedKey);
    const uiFilters: Record<string, any> = {};
    const page = parsePositiveInt(params.get('page'), DEFAULT_PAGE);
    const size = parsePositiveInt(params.get('size'), DEFAULT_SIZE);
    const orderBy = params.get('orderBy') || null;
    const isDesc = parseBooleanParam(params.get('desc'), true);
    const sortOrder = parseSortOrder(params.get('sortOrder'), orderBy, isDesc);
    const selectedBranchId = parseOptionalInt(params.get('branchId'));
    const fullName = getMultiParams(params, 'fullName');
    const startRange = parseDateTimeRange(params.get('startFrom'), params.get('startTo'));

    if (fullName.length) uiFilters.fullName = fullName;

    return {
        hasAny: params.toString().length > 0,
        page,
        size,
        orderBy,
        isDesc,
        sortOrder,
        selectedBranchId,
        filterFullName: toFilterList('UserName', fullName),
        filterDateTime: startRange?.startDate ?? null,
        filterEndDateTime: startRange?.endDate ?? null,
        startTime: startRange?.startTime ?? null,
        endTime: startRange?.endTime ?? null,
        hasCustomDateRange: !!startRange,
        uiFilters,
    };
}

export function buildCallsStatisticsSearchParams(
    state: CallsStatisticsUrlWriteState,
    encodedKey: string = CALLS_ENCODED_QUERY_KEY
): URLSearchParams {
    return wrapEncodedSearchParams(buildPlainCallsStatisticsSearchParams(state), encodedKey);
}

function buildPlainCallsStatisticsSearchParams(state: CallsStatisticsUrlWriteState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.page && state.page !== DEFAULT_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.size && state.size !== DEFAULT_SIZE) {
        params.set('size', String(state.size));
    }
    if (state.orderBy && !(state.orderBy === 'Id' && state.sortOrder === 0)) {
        params.set('orderBy', state.orderBy);
        params.set('desc', state.isDesc ? '1' : '0');
        if (state.sortOrder) {
            params.set('sortOrder', String(state.sortOrder));
        }
    }
    if (state.selectedBranchId != null) {
        params.set('branchId', String(state.selectedBranchId));
    }

    state.filterFullName?.forEach((item) => {
        if (item?.value) {
            params.append('fullName', item.value);
        }
    });

    const startFrom = combineDateAndTime(state.filterDateTime, state.startTime, true);
    const startTo = combineDateAndTime(state.filterEndDateTime, state.endTime, false);
    if (startFrom && startTo && !isDefaultDateRange(startFrom, startTo)) {
        params.set('startFrom', toLocalIso(startFrom));
        params.set('startTo', toLocalIso(startTo));
    }

    return params;
}

function isDefaultDateRange(startFrom: Date, startTo: Date): boolean {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 0);
    return toLocalIso(startFrom) === toLocalIso(todayStart) && toLocalIso(startTo) === toLocalIso(todayEnd);
}

function getMultiParams(params: URLSearchParams, key: string): string[] {
    const values: string[] = [];
    params.getAll(key).forEach((value) => {
        value.split(',').forEach((part) => {
            const trimmed = part.trim();
            if (trimmed) values.push(trimmed);
        });
    });
    return values;
}

function toFilterList(key: string, values?: string[]): CallsStatisticsFilterItem[] | null {
    if (!values?.length) return null;
    return values.map((value) => ({ key, value }));
}

function parsePositiveInt(value: string | null, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return fallback;
    }
    return parsed;
}

function parseOptionalInt(value: string | null): number | null {
    if (value == null || value === '') return null;
    const parsed = Number(value);
    if (!Number.isInteger(parsed)) return null;
    return parsed;
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
    if (value == null) return fallback;
    return value === '1' || value === 'true';
}

function parseSortOrder(value: string | null, orderBy: string | null, isDesc: boolean): number {
    if (value === '1' || value === '-1' || value === '0') {
        return Number(value);
    }
    if (!orderBy) return 0;
    return isDesc ? -1 : 1;
}

function toLocalIso(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

function fromLocalIso(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
    if (!match) {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
        Number(match[6])
    );
}

function parseDateTimeRange(fromValue: string | null, toValue: string | null) {
    if (!fromValue || !toValue) return null;
    const from = fromLocalIso(fromValue);
    const to = fromLocalIso(toValue);
    if (!from || !to) return null;

    return {
        startDate: startOfDay(from),
        endDate: startOfDay(to),
        startTime: timeOnly(from),
        endTime: timeOnly(to),
    };
}

function startOfDay(date: Date): Date {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
}

function timeOnly(date: Date): Date {
    const next = new Date();
    next.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0);
    return next;
}

function combineDateAndTime(dateValue: any, timeValue: any, isStart: boolean): Date | null {
    const date = toValidDate(dateValue);
    if (!date) return null;
    const time = toValidDate(timeValue);
    const result = new Date(date);
    if (time) {
        result.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
    } else {
        result.setHours(isStart ? 0 : 23, isStart ? 0 : 59, isStart ? 0 : 59, 0);
    }
    return result;
}

function toValidDate(value: any): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
